import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiCall from '../../../api';
import { toast } from 'react-toastify';
import i18n from '../../../translation/i18n.js';

// Types
interface PersonalData {
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    incomeTax: number;
    phoneNo: string;
    ibanNumber: string;
    bicNumber: string;
    bankAccounts: Array<{
        bank: string;
        clearingNumber: string;
        accountNumber: string;
    }>;
    isVerified: boolean;
    dateOfBirth: string;
    bank: string;
    clearingNumber: string;
    accountNumber: string;
    customCheck: boolean;
    accountType: number;
    ssn: string;
    profession: string;
    country: string;
    city: string;
    address: string;
    zipCode: string;
    profileImage: string;
    createdDate: string;
}

interface TaxInformation {
    id: number;
    newTaxValue: number;
    oldTaxValue: number
    requestStatus: string;
    userId: number;
    createdDate: string;
    updatedDate: string | null;
}

interface UserPreferences {
    id: number;
    userId: number;
    organizationName: string;
    logo: string | null;
    currencies: string[] | [];
    createdDate: string;
    updatedDate: string | null;
}

interface ProfileSettingsState {
    personalData: PersonalData;
    taxInformation: TaxInformation | null;
    userPreferences: UserPreferences | null;
    isError: boolean;
    isLoading: boolean;
    message: string;
    isSuccess: boolean;
}

interface UpdatePersonalDataPayload {
    userData: Partial<PersonalData>;
}
const t = (key: string): string => String(i18n.t(key));

const initialState: ProfileSettingsState = {
    personalData: {
        firstName: '',
        lastName: '',
        fullName: '',
        email: '',
        phoneNo: '',
        ssn: '',
        incomeTax: 0,
        ibanNumber: '',
        bicNumber: '',
        bankAccounts: [],
        customCheck: false,
        isVerified: false,
        bank: '',
        clearingNumber: '',
        accountNumber: '',
        accountType: 0,
        dateOfBirth: '',
        profession: '',
        country: '',
        city: '',
        address: '',
        zipCode: '',
        profileImage: '',
        createdDate: ''
    },
    taxInformation: null,
    userPreferences: null,
    isError: false,
    isLoading: false,
    message: '',
    isSuccess: false,
};

// Async thunk for fetching personal data
export const fetchPersonalData = createAsyncThunk(
    'profileSettings/fetchPersonalData',
    async (userId: string, thunkAPI) => {
        try {
            if (!userId) {
                return thunkAPI.rejectWithValue('User ID is required');
            }

            const res = await apiCall.get(`/employee/get/${userId}`);

            if (!(res.data.statusCode === 200 || res.data.statusCode === 201)) {
                return thunkAPI.rejectWithValue(res.data.message);
            }

            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to fetch personal data:', message);
            toast.error(t('PROFILE_DATA_LOAD_FAILED'));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Async thunk for updating personal data
export const updatePersonalData = createAsyncThunk(
    'profileSettings/updatePersonalData',
    async ({ userData }: UpdatePersonalDataPayload, thunkAPI) => {
        try {
            const res = await apiCall.put(`/employee/update`, userData);

            if (!(res.data.statusCode === 200 || res.data.statusCode === 201)) {
                return thunkAPI.rejectWithValue(res.data.message);
            }

            toast.success(t('PROFILE_DATA_UPDATED'));
            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to update personal data:', message);
            toast.error(t('PROFILE_DATA_UPDATE_FAILED'));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Async thunk for updating bank data
export const updateBankData = createAsyncThunk(
    'profileSettings/updateBankData',
    async ({ userId, bankData }: { userId: string; bankData: { bank: string; clearingNumber: string; accountNumber: string; code?: string; } }, thunkAPI) => {
        try {
            console.log('Updating bank data with:', bankData);
            if (!userId) {
                return thunkAPI.rejectWithValue('User ID is required');
            }

            const res = await apiCall.put(`/settings/save-customer-salary`, bankData);

            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to update bank data:', message);
            // toast.error(t('BANK_DETAILS_UPDATE_FAILED'));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const removeBankAccount = createAsyncThunk(
    'profileSettings/removeBankAccount',
    async (_, thunkAPI) => {
        try {
            const token = localStorage.getItem('token');
            const res = await apiCall.delete(`/employee/bank-details`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (!(res.data.statusCode === 200 || res.data.statusCode === 201)) {
                return thunkAPI.rejectWithValue(res.data.message);
            }

            toast.success(t('BANK_ACCOUNT_REMOVED'));
            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to remove bank account:', message);
            toast.error(t('BANK_ACCOUNT_REMOVE_FAILED'));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Async thunk for fetching tax information
export const fetchTaxInformation = createAsyncThunk(
    'profileSettings/fetchTaxInformation',
    async (_, thunkAPI) => {
        try {
            const token = localStorage.getItem('token');
            const res = await apiCall.get(`/incometax`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'accept': '*/*'
                }
            });
            console.log('Tax information response:', res.data);
            if (!(res.data.statusCode === 200 || res.data.statusCode === 201)) {
                return thunkAPI.rejectWithValue(res.data.message);
            }

            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to fetch tax information:', message);
            toast.error(t('TAX_INFORMATION_LOAD_FAILED'));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Async thunk for fetching user preferences
export const fetchUserPreferences = createAsyncThunk(
    'profileSettings/fetchUserPreferences',
    async (_, thunkAPI) => {
        try {
            const token = localStorage.getItem('token');
            const res = await apiCall.get(`/userpreference`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'accept': '*/*'
                }
            });

            if (!(res.data.statusCode === 200 || res.data.statusCode === 201)) {
                return thunkAPI.rejectWithValue(res.data.message);
            }

            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to fetch user preferences:', message);
            toast.error(t('USER_PREFERENCES_LOAD_FAILED'));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Async thunk for updating user preferences
export const updateUserPreferences = createAsyncThunk(
    'profileSettings/updateUserPreferences',
    async ({ organizationName, logo, currencies }: { organizationName: string; logo?: File; currencies: string[] }, thunkAPI) => {
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();

            formData.append('OrganizationName', organizationName);

            if (logo) {
                formData.append('Logo', logo);
            }

            // Append each currency separately
            currencies.forEach(currency => {
                formData.append('Currency', currency);
            });

            const res = await apiCall.post(`/userpreference`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'accept': '*/*',
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (!(res.data.statusCode === 200 || res.data.statusCode === 201)) {
                return thunkAPI.rejectWithValue(res.data.message);
            }

            toast.success(t('PREFERENCES_SAVED'));
            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to update user preferences:', message);
            toast.error(t('PREFERENCES_SAVE_FAILED'));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Async thunk for updating tax information
export const updateTaxInformation = createAsyncThunk(
    'profileSettings/updateTaxInformation',
    async ({ taxPercentage, file, language = 'sv-se' }: { taxPercentage: number; file?: File; language?: string }, thunkAPI) => {
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();

            if (file) {
                formData.append('file', file);
            }

            const res = await apiCall.post(`/incometax?taxPercentage=${taxPercentage}&language=${language}`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'accept': '*/*',
                    'Content-Type': 'multipart/form-data'
                }
            });
            console.log('Updated Tax information response:', res.data.message, ([400].includes(res.data.statusCode)));
            if (
                res.data.statusCode === 400 &&
                res.data.message === "To make a tax deduction, please upload the required file and try again!"
            ) {
                console.log('Triggering toast for missing file requirement');
                toast.error(t('TAX_FILE_REQUIRED'));
            }

            if (!(res.data.statusCode === 200 || res.data.statusCode === 201)) {
                return thunkAPI.rejectWithValue(res.data.message);
            }

            toast.success(t('TAX_CHANGE_REQUEST_SUBMITTED'));
            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to update tax information:', message);
            toast.error(t('TAX_CHANGE_REQUEST_FAILED'));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

const profileSettingsSlice = createSlice({
    name: 'profileSettings',
    initialState,
    reducers: {
        reset: (state) => {
            state.isLoading = false;
            state.isError = false;
            state.isSuccess = false;
            state.message = '';
        },
        resetMessages: (state) => {
            state.isError = false;
            state.isSuccess = false;
            state.message = '';
        },
        updatePersonalDataLocally: (state, action) => {
            state.personalData = { ...state.personalData, ...action.payload };
        },
        setPersonalData: (state, action) => {
            state.personalData = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Personal Data
            .addCase(fetchPersonalData.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(fetchPersonalData.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.personalData = {
                    firstName: action.payload?.firstName || '',
                    lastName: action.payload?.lastName || '',
                    fullName: action.payload?.fullName || '',
                    email: action.payload?.email || '',
                    phoneNo: action.payload?.phoneNo || '',
                    ssn: action.payload?.ssn || '',
                    incomeTax: action.payload?.incomeTax,
                    bicNumber: action.payload?.customerSalaryInformation?.[0]?.bicNumber || '',
                    ibanNumber: action.payload?.customerSalaryInformation?.[0]?.ibanNumber || '',
                    bankAccounts: action.payload?.customerSalaryInformation || [],
                    bank: action.payload?.customerSalaryInformation?.[0]?.bank || '',
                    clearingNumber: action.payload?.customerSalaryInformation?.[0]?.clearingNumber || '',
                    accountNumber: action.payload?.customerSalaryInformation?.[0]?.accountNumber || '',
                    isVerified: action.payload?.isVerified || false,
                    dateOfBirth: action.payload?.dateOfBirth || '',
                    accountType: action.payload?.accountType || 0,
                    profession: action.payload?.profession || '',
                    country: action.payload?.country || '',
                    customCheck: action.payload?.customCheck || false,
                    city: action.payload?.city || '',
                    address: action.payload?.address || '',
                    zipCode: action.payload?.zipCode || '',
                    profileImage: action.payload?.profileImage || '',
                    createdDate: action.payload?.createdDate || ''
                };
                console.log(state.personalData, 'state personal Data')
            })
            .addCase(fetchPersonalData.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            })
            // Update Personal Data
            .addCase(updatePersonalData.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(updatePersonalData.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.personalData = { ...state.personalData, ...action.payload };
            })
            .addCase(updatePersonalData.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            })
            .addCase(updateBankData.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(updateBankData.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.personalData.bank = action.payload?.bank || state.personalData.bank;
                state.personalData.clearingNumber = action.payload?.clearingNumber || state.personalData.clearingNumber;
                state.personalData.accountNumber = action.payload?.accountNumber || state.personalData.accountNumber;
                toast.success(t('BANK_DETAILS_UPDATED'));
            })
            .addCase(updateBankData.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
                toast.error(t('BANK_DETAILS_UPDATE_FAILED'));
            })
            .addCase(removeBankAccount.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(removeBankAccount.fulfilled, (state) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.personalData.bank = '';
                state.personalData.clearingNumber = '';
                state.personalData.accountNumber = '';
                state.personalData.bankAccounts = [];
            })
            .addCase(removeBankAccount.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            })
            // Fetch Tax Information
            .addCase(fetchTaxInformation.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(fetchTaxInformation.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.taxInformation = {
                    id: action.payload?.id || 0,
                    newTaxValue: action.payload?.newTaxValue || 30,
                    oldTaxValue: action.payload?.oldTaxValue || 30,
                    requestStatus: action.payload?.requestStatus || '',
                    userId: action.payload?.userId || 0,
                    createdDate: action.payload?.createdDate || '',
                    updatedDate: action.payload?.updatedDate || null
                };
                state.message = action.payload.message || '';
            })
            .addCase(fetchTaxInformation.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            })
            // Update Tax Information
            .addCase(updateTaxInformation.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(updateTaxInformation.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.taxInformation = {
                    id: action.payload?.id || 0,
                    newTaxValue: action.payload?.newTaxValue || 30,
                    oldTaxValue: action.payload?.oldTaxValue || 30,
                    requestStatus: action.payload?.requestStatus || 'Pending',
                    userId: action.payload?.userId || 0,
                    createdDate: action.payload?.createdDate || '',
                    updatedDate: action.payload?.updatedDate || null
                };
            })
            .addCase(updateTaxInformation.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            })
            // Fetch User Preferences
            .addCase(fetchUserPreferences.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(fetchUserPreferences.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.userPreferences = {
                    id: action.payload?.id || 0,
                    userId: action.payload?.userId || 0,
                    organizationName: action.payload?.organizationName || '',
                    logo: action.payload?.logo || null,
                    currencies: action.payload?.currencies || [],
                    createdDate: action.payload?.createdDate || '',
                    updatedDate: action.payload?.updatedDate || null
                };
            })
            .addCase(fetchUserPreferences.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            })
            // Update User Preferences
            .addCase(updateUserPreferences.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(updateUserPreferences.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.userPreferences = {
                    id: action.payload?.id || state.userPreferences?.id || 0,
                    userId: action.payload?.userId || state.userPreferences?.userId || 0,
                    organizationName: action.payload?.organizationName || '',
                    logo: action.payload?.logo || state.userPreferences?.logo || null,
                    currencies: action.payload?.currencies || [],
                    createdDate: action.payload?.createdDate || state.userPreferences?.createdDate || '',
                    updatedDate: action.payload?.updatedDate || null
                };
            })
            .addCase(updateUserPreferences.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            });
    },
});

export const {
    reset,
    resetMessages,
    updatePersonalDataLocally,
    setPersonalData
} = profileSettingsSlice.actions;

export default profileSettingsSlice.reducer;

// Export types for use in components
export type { PersonalData, ProfileSettingsState, UpdatePersonalDataPayload, TaxInformation, UserPreferences };